import React, { FC, memo } from 'react';
import tmpCourseFullImage from 'src/assets/images/tmp-course-full-image-final.png';
import styled, { css } from 'styled-components';
import { colors } from 'src/config/colors';
import { typography } from 'src/config/typography';
import { rem } from 'polished';
import { gutter } from 'src/config/layout';
import { CourseHeaderInfo } from 'src/common/components/CourseHeaderInfo';
import { media } from 'src/config/breakpoints';
import { Course } from 'src/common/models/course';
import { CourseEvent } from 'src/common/models/event';
import Image from 'next/image';

const Container = styled.div`
  background: ${colors.grey04};
  border-radius: 0.25rem;
  display: grid;
  grid-template-areas:
    'badges image'
    'title title'
    'info info';
  grid-template-columns: 44% 56%;
  padding: 0.5rem 0.875rem 0 0.625rem;

  ${media.w.min.px768(css`
    border-radius: 0.25rem 0.25rem 0 0;
    grid-template-areas:
      'badges image'
      'title image'
      'info image';
    grid-template-columns: 58% 42%;
    margin: 0 -${rem(gutter)};
    padding: 1.0625rem 1.375rem 1.375rem 2.75rem;
  `)};

  ${media.w.min.px992(css`
    grid-template-columns: 60% 40%;
    padding-left: 1.375rem;
  `)};

  ${media.w.min.px1200(css`
    grid-template-columns: 55% 45%;
    padding-left: 2.75rem;
  `)};
`;

const ImageWrapper = styled.div`
  align-items: center;
  display: flex;
  grid-area: image;
  justify-content: flex-end;
  margin-top: -1rem;

  ${media.w.min.px768(css`
    margin-top: 0;
    padding-bottom: 4rem;
  `)};

  img {
    max-width: 100%;
  }
`;

const StyledCourseHeaderInfo = styled(CourseHeaderInfo)`
  grid-area: info;
`;

const Title = styled.div`
  font-family: ${typography.fontFamilyHeaders};
  font-size: 1rem;
  grid-area: title;
  line-height: 1.375rem;
  margin: 0.3125rem 0 0.625rem;

  ${media.w.min.px768(css`
    font-size: 2.3125rem;
    line-height: 2.875rem;
    margin: 1.875rem 0 1.125rem;
  `)};
`;

export interface CourseHeaderProps {
  course: Course;
  selectedCourseEvent?: CourseEvent;
}

export const CourseHeader: FC<CourseHeaderProps> = memo(({ course, selectedCourseEvent }) => (
  <Container>
    <Title>{course.title}</Title>
    <StyledCourseHeaderInfo course={course} selectedCourseEvent={selectedCourseEvent} />
    <ImageWrapper>
      <Image
        src={course.image.url ?? tmpCourseFullImage}
        alt="Course image"
        width={course.image.width}
        height={course.image.height}
      />
    </ImageWrapper>
  </Container>
));
CourseHeader.displayName = 'CourseHeader';
